/**
 * MapLibreCompat — a react-leaflet-shaped API implemented on MapLibre GL JS.
 *
 * WHY THIS EXISTS
 * ---------------
 * The dispatch map (MapPage.tsx, 2077 lines) and the schedule map (SchedulePage.tsx)
 * were built against react-leaflet + raster tiles. Migrating them to MapLibre GL by
 * rewriting the JSX would have meant touching every one of the ~40 map features we
 * shipped over the last month — live WS marker interpolation, dead-reckoning, stop
 * markers, uncertainty circles, road-matched route polylines, flight segments,
 * weather overlays, the units toggle, tech follow. Every one of those is a
 * regression opportunity.
 *
 * Instead this module reimplements the *exact* Leaflet API surface those two pages
 * use (measured: MapContainer, TileLayer, Marker, Popup, Polyline, Circle,
 * LayersControl, useMap, L.divIcon, L.icon, L.latLngBounds, and 5 map methods) on
 * top of maplibre-gl. The pages change their import line and nothing else material.
 *
 * WHAT WE ACTUALLY GAIN
 * ---------------------
 *  - The basemap becomes VECTOR (our own /tiles-gl/ pbf tiles) instead of raster
 *    PNGs. One tileset serves every zoom; measured ~5-8x less data over the wire.
 *  - Real overzoom: our tiles stop at z14 but vector overzooms cleanly to z22.
 *    The raster path was serving upscaled mush past z14.
 *  - Shapes go through TWO rAF-batched GeoJSON sources instead of one DOM/canvas
 *    path per polyline, which is strictly faster than Leaflet for the hundreds of
 *    circles a flight trail produces.
 *  - Zero third-party calls: style, glyphs, sprites, tiles all from our nginx.
 *
 * COORDINATE ORDER WARNING
 * ------------------------
 * Leaflet is [lat, lng]. MapLibre is [lng, lat]. Every public function in this file
 * takes LEAFLET order (so the pages don't change) and flips internally. The only
 * place you see [lng, lat] is inside this file.
 */
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import {
  createContext, useContext, useEffect, useRef, useState,
  Children, isValidElement, Fragment,
  type ReactNode, type ReactElement, type CSSProperties,
} from 'react';
import { createPortal } from 'react-dom';

/**
 * The shared style. Served from OUR host, and deliberately a URL rather than an
 * inline object so the Android app can consume the byte-identical style later and
 * get a byte-identical-looking map (that's the seamless web+app goal).
 */
export const SHARED_STYLE_URL = '/map-style/concept-v2.json';
export const SHARED_STYLE_URL_NIGHT = '/map-style/concept-v2-night.json';

/**
 * Fetch the shared style and make its URLs absolute against the current origin.
 *
 * The style file on disk is deliberately RELATIVE (/tiles-gl/...), so the exact
 * same file works from backoffice.concept.bz, from a dev host, and from the
 * Android app pointed at its own base URL. MapLibre GL JS, however, refuses to
 * parse a relative `sprite` URL — it throws "Unable to parse URL" and aborts the
 * ENTIRE style load, which silently yields a blank map with zero tile requests.
 * (Relative `glyphs` and relative source `tiles` are fine. Only `sprite` isn't.)
 *
 * So: resolve here, at load, against whatever origin we happen to be served from.
 */
export async function loadSharedStyle(url: string = SHARED_STYLE_URL, origin?: string): Promise<any> {
  const base = origin || window.location.origin;
  const abs = (u: string) => (/^https?:\/\//i.test(u) ? u : base + (u.startsWith('/') ? u : '/' + u));

  const res = await fetch(abs(url), { credentials: 'same-origin' });
  if (!res.ok) throw new Error('map style ' + res.status + ' from ' + url);
  const style = await res.json();

  if (typeof style.sprite === 'string') style.sprite = abs(style.sprite);
  if (typeof style.glyphs === 'string') style.glyphs = abs(style.glyphs);
  for (const key of Object.keys(style.sources || {})) {
    const src = style.sources[key];
    if (src && Array.isArray(src.tiles)) src.tiles = src.tiles.map(abs);
    if (src && typeof src.url === 'string') src.url = abs(src.url);
  }
  return style;
}

// ─────────────────────────────────────────────────────────────────────────────
// BASEMAP / OVERLAY / THEME CONTROLLER  (Street · Topo · Satellite · 3D)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Everything here is done by MUTATING the live style — adding/removing sources and
 * layers, flipping visibility, patching paint. Deliberately NOT map.setStyle().
 *
 * setStyle() would diff away the cm-lines / cm-fills sources that MapLibreCompat
 * installs on load, taking every route polyline, uncertainty circle and flight trail
 * with them. Mutating in place keeps all of that alive across a basemap switch.
 *
 * Capability comes from /map-style/layers.json at runtime, so when the Topo or
 * Satellite tilesets land we flip one boolean in a JSON file and the buttons light up.
 * No frontend rebuild.
 */
export const LAYERS_MANIFEST_URL = '/map-style/layers.json';
export const THEME_PATCH_URL = '/map-style/theme-patch.json';

export interface BasemapDef {
  id: string; label: string; available: boolean; kind?: string;
  sources?: Record<string, any>; description?: string;
}
export interface OverlayDef {
  id: string; label: string; available: boolean;
  styleLayers?: string[]; hidesLayers?: string[]; pitch?: number; bearing?: number;
  needs?: string; description?: string;
}
export interface LayersManifest { basemaps: BasemapDef[]; overlays: OverlayDef[]; }

export async function loadLayersManifest(origin?: string): Promise<LayersManifest> {
  const base = origin || window.location.origin;
  const r = await fetch(base + LAYERS_MANIFEST_URL, { credentials: 'same-origin' });
  if (!r.ok) throw new Error('layers manifest ' + r.status);
  return r.json();
}

/** The layers a raster/satellite basemap has to sit UNDER, and the ones it replaces. */
const BASE_FILL_LAYERS = ['background', 'landuse_residential', 'landcover_wood', 'park', 'water', 'building'];
const SAT_INSERT_BEFORE = 'waterway';   // above bg fills, below every line + label

export class BasemapController {
  private basemap = 'street';
  private overlays = new Set<string>();
  private theme: 'day' | 'night' = 'day';
  private patch: any = null;

  constructor(private map: maplibregl.Map, private manifest: LayersManifest) {}

  getBasemap() { return this.basemap; }
  getOverlays() { return [...this.overlays]; }
  getTheme() { return this.theme; }

  private def(id: string) { return this.manifest.basemaps.find(b => b.id === id); }
  private ov(id: string) { return this.manifest.overlays.find(o => o.id === id); }

  private abs(u: string) {
    return /^https?:\/\//i.test(u) ? u : window.location.origin + (u.startsWith('/') ? u : '/' + u);
  }

  private addSources(defn: BasemapDef | OverlayDef) {
    const srcs = (defn as BasemapDef).sources || {};
    for (const [id, raw] of Object.entries(srcs)) {
      if (this.map.getSource(id)) continue;
      const src: any = { ...raw };
      if (Array.isArray(src.tiles)) src.tiles = src.tiles.map((t: string) => this.abs(t));
      this.map.addSource(id, src);
    }
  }

  /** Street | Topo | Satellite. Unavailable ids are a no-op (the UI greys them out anyway). */
  setBasemap(id: string) {
    const d = this.def(id);
    if (!d || !d.available || id === this.basemap) return;

    // tear down whatever the previous basemap added
    if (this.map.getLayer('sat-raster')) this.map.removeLayer('sat-raster');
    if (this.map.getLayer('contour-line')) this.map.removeLayer('contour-line');
    if (this.map.getLayer('contour-label')) this.map.removeLayer('contour-label');
    if (this.map.getLayer('hillshade')) this.map.removeLayer('hillshade');
    this.map.setTerrain(null as any);
    BASE_FILL_LAYERS.forEach(l => this.setVis(l, true));

    this.basemap = id;
    if (id === 'street') return;

    this.addSources(d);

    if (id === 'satellite') {
      // imagery replaces the painted ground, but roads/labels stay on top — that is what
      // makes a satellite view actually navigable rather than just pretty.
      this.map.addLayer(
        { id: 'sat-raster', type: 'raster', source: 'sat', paint: { 'raster-opacity': 1 } } as any,
        this.map.getLayer(SAT_INSERT_BEFORE) ? SAT_INSERT_BEFORE : undefined,
      );
      ['background', 'landuse_residential', 'landcover_wood', 'park', 'water', 'building']
        .forEach(l => this.setVis(l, false));
    }

    if (id === 'topo') {
      this.map.addLayer(
        { id: 'hillshade', type: 'hillshade', source: 'terrain',
          paint: { 'hillshade-exaggeration': 0.45, 'hillshade-shadow-color': '#5a5348' } } as any,
        this.map.getLayer('waterway') ? 'waterway' : undefined,
      );
      this.map.addLayer(
        { id: 'contour-line', type: 'line', source: 'contours', 'source-layer': 'contour',
          paint: {
            'line-color': '#9c8f6f',
            'line-width': ['case', ['==', ['%', ['get', 'ele'], 100], 0], 1.1, 0.5],
            'line-opacity': 0.55,
          } } as any,
        this.map.getLayer('waterway') ? 'waterway' : undefined,
      );
      this.map.addLayer({
        id: 'contour-label', type: 'symbol', source: 'contours', 'source-layer': 'contour',
        minzoom: 12,
        filter: ['==', ['%', ['get', 'ele'], 100], 0],
        layout: { 'symbol-placement': 'line', 'text-field': ['concat', ['get', 'ele'], "'"],
                  'text-font': ['Noto Sans Regular'], 'text-size': 10 },
        paint: { 'text-color': '#7d7358', 'text-halo-color': '#ffffff', 'text-halo-width': 1 },
      } as any);
    }
  }

  private setVis(layer: string, on: boolean) {
    if (this.map.getLayer(layer)) this.map.setLayoutProperty(layer, 'visibility', on ? 'visible' : 'none');
  }

  setOverlay(id: string, on: boolean) {
    const o = this.ov(id);
    if (!o || !o.available) return;
    on ? this.overlays.add(id) : this.overlays.delete(id);

    (o.styleLayers || []).forEach(l => this.setVis(l, on));
    (o.hidesLayers || []).forEach(l => this.setVis(l, !on));

    if (id === 'buildings3d') {
      // 3D is a camera change as much as a layer change — flat extrusions look like a bug.
      this.map.easeTo({
        pitch: on ? (o.pitch ?? 60) : 0,
        bearing: on ? (o.bearing ?? -17) : 0,
        duration: 900,
      });
    }
    if (id === 'hillshade' && this.map.getSource('terrain')) {
      this.map.setTerrain(on ? ({ source: 'terrain', exaggeration: 1.2 } as any) : (null as any));
    }
  }

  /** Day <-> night by patching paint properties. No setStyle, so nothing is torn down. */
  async setTheme(t: 'day' | 'night') {
    if (t === this.theme) return;
    if (!this.patch) {
      const r = await fetch(this.abs(THEME_PATCH_URL), { credentials: 'same-origin' });
      this.patch = await r.json();
    }
    const p = this.patch[t] || {};
    for (const [layer, paint] of Object.entries(p as Record<string, any>)) {
      if (!this.map.getLayer(layer)) continue;
      for (const [k, v] of Object.entries(paint as Record<string, any>)) {
        try { this.map.setPaintProperty(layer, k, v); } catch { /* layer lacks that prop */ }
      }
    }
    this.theme = t;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Types + the `L` shim
// ─────────────────────────────────────────────────────────────────────────────

export type LatLng = [number, number];           // [lat, lng] — Leaflet order

export interface IconDescriptor {
  __icon: true;
  html?: string;
  iconUrl?: string;
  iconSize: [number, number];
  iconAnchor: [number, number];
  popupAnchor?: [number, number];
  className?: string;
}

export interface PathOptions {
  color?: string;
  weight?: number;
  opacity?: number;
  fillColor?: string;
  fillOpacity?: number;
  dashArray?: string;
  /** Leaflet fills a CLOSED polyline when this is true — the NWS alert polygons rely on it. */
  fill?: boolean;
}

export class LatLngBounds {
  constructor(public pts: LatLng[]) {}
  /** Leaflet's .pad(ratio) — grow the box by a fraction of its own size. */
  pad(ratio: number): LatLngBounds {
    if (!this.pts.length) return this;
    const lats = this.pts.map(p => p[0]);
    const lngs = this.pts.map(p => p[1]);
    let s = Math.min(...lats), n = Math.max(...lats);
    let w = Math.min(...lngs), e = Math.max(...lngs);
    // A zero-size box (single point) has nothing to pad by proportionally, so give
    // it a small absolute box — otherwise fitBounds would slam to max zoom.
    const dLat = (n - s) || 0.002;
    const dLng = (e - w) || 0.002;
    s -= dLat * ratio; n += dLat * ratio;
    w -= dLng * ratio; e += dLng * ratio;
    return new LatLngBounds([[s, w], [n, e]]);
  }
  toMaplibre(): [[number, number], [number, number]] {
    const lats = this.pts.map(p => p[0]);
    const lngs = this.pts.map(p => p[1]);
    return [
      [Math.min(...lngs), Math.min(...lats)],   // SW  [lng, lat]
      [Math.max(...lngs), Math.max(...lats)],   // NE
    ];
  }
  isValid() { return this.pts.length > 0; }
}

/**
 * The Leaflet-shaped map handle the pages hold in `mapRef` and get from useMap().
 * Only the methods the pages actually call are implemented — deliberately, so a
 * page that starts using a new one fails loudly at compile time instead of
 * silently no-op'ing at runtime.
 */
export class LeafletMap {
  constructor(public raw: maplibregl.Map) {}

  /**
   * Leaflet's setView(center, zoom, {animate, duration}). The 3rd arg matters:
   * MapPage uses it on every "a tech just reported, follow them" path, and
   * ignoring it would turn a smooth pan into a teleport.
   */
  setView(center: LatLng, zoom?: number, opts?: { animate?: boolean; duration?: number }) {
    const target: any = { center: [center[1], center[0]] };
    if (zoom != null) target.zoom = zoom;
    if (opts && opts.animate) {
      target.duration = (opts.duration == null ? 0.5 : opts.duration) * 1000;  // Leaflet: seconds
      this.raw.easeTo(target);
    } else {
      this.raw.jumpTo(target);
    }
    return this;
  }
  flyTo(center: LatLng, zoom?: number, opts?: { animate?: boolean; duration?: number }) {
    const duration = opts?.animate === false ? 0 : (opts?.duration ?? 1.2) * 1000;  // Leaflet: seconds
    this.raw.flyTo({ center: [center[1], center[0]], ...(zoom != null ? { zoom } : {}), duration });
    return this;
  }
  /**
   * Accepts either a LatLngBounds (from L.latLngBounds(...).pad(...)) or Leaflet's
   * other accepted form — a raw [[swLat,swLng],[neLat,neLng]] corner array. MapPage
   * uses BOTH, so both must work or the flight/ground auto-fit silently breaks.
   */
  fitBounds(bounds: LatLngBounds | LatLng[], opts?: { maxZoom?: number; padding?: number }) {
    const b = Array.isArray(bounds) ? new LatLngBounds(bounds) : bounds;
    if (!b || !b.isValid()) return this;
    this.raw.fitBounds(b.toMaplibre(), {
      maxZoom: opts?.maxZoom ?? 18,
      padding: opts?.padding ?? 40,
      duration: 600,
    });
    return this;
  }
  getZoom() { return this.raw.getZoom(); }
  getCenter() { const c = this.raw.getCenter(); return { lat: c.lat, lng: c.lng }; }
  /** Leaflet's invalidateSize() — recompute container size after a layout change. */
  invalidateSize() { this.raw.resize(); return this; }

  /**
   * Event passthrough. MapLibre's event names for the ones the pages need are the
   * same as Leaflet's ('movestart','dragstart','zoomstart','rotatestart','pitchstart').
   *
   * CRITICAL for follow-mode: a USER gesture carries `e.originalEvent`; a programmatic
   * flyTo/easeTo/jumpTo does NOT. That is the only reliable way to tell "the dispatcher
   * grabbed the map" apart from "we just recentred it ourselves" — without it, our own
   * follow animation would instantly cancel follow mode on the first tick.
   */
  on(ev: string, fn: (e: any) => void) { this.raw.on(ev as any, fn); return this; }
  off(ev: string, fn: (e: any) => void) { this.raw.off(ev as any, fn); return this; }

  /** Camera controls for the 2D/3D toggle. */
  getPitch() { return this.raw.getPitch(); }
  getBearing() { return this.raw.getBearing(); }
  easeCamera(opts: { pitch?: number; bearing?: number; duration?: number }) {
    this.raw.easeTo({ ...opts, duration: opts.duration ?? 600 });
    return this;
  }
}

/** The pieces of the `L` namespace the two pages use. Drop-in for `import L from 'leaflet'`. */
export const L = {
  divIcon: (o: { html?: string; iconSize?: [number, number]; iconAnchor?: [number, number]; popupAnchor?: [number, number]; className?: string }): IconDescriptor => ({
    __icon: true,
    html: o.html,
    iconSize: o.iconSize ?? [24, 24],
    iconAnchor: o.iconAnchor ?? [(o.iconSize?.[0] ?? 24) / 2, (o.iconSize?.[1] ?? 24) / 2],
    popupAnchor: o.popupAnchor,
    className: o.className,
  }),
  icon: (o: { iconUrl: string; iconSize?: [number, number]; iconAnchor?: [number, number]; popupAnchor?: [number, number]; [k: string]: any }): IconDescriptor => ({
    __icon: true,
    iconUrl: o.iconUrl,
    iconSize: o.iconSize ?? [25, 41],
    iconAnchor: o.iconAnchor ?? [12, 41],
    popupAnchor: o.popupAnchor,
  }),
  latLngBounds: (pts: LatLng[]) => new LatLngBounds(pts),
};

let DEFAULT_ICON: IconDescriptor = L.divIcon({
  className: 'cm',
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34],
  html: '<svg width="25" height="41" viewBox="0 0 25 41"><path d="M12.5 0C5.6 0 0 5.6 0 12.5 0 22 12.5 41 12.5 41S25 22 25 12.5C25 5.6 19.4 0 12.5 0z" fill="#2A81CB"/><circle cx="12.5" cy="12.5" r="4.5" fill="#fff"/></svg>',
});
/** Mirrors Leaflet's `L.Marker.prototype.options.icon = ...` global default. */
export function setDefaultMarkerIcon(icon: IconDescriptor) { DEFAULT_ICON = icon; }

// ─────────────────────────────────────────────────────────────────────────────
// Overlay shape plumbing
//
// Every Polyline lives in ONE GeoJSON source ('cm-lines'); every Circle lives in
// ONE source ('cm-fills'). Features are keyed by an auto id and the whole
// FeatureCollection is re-set at most once per animation frame. That's why adding
// 300 flight-trail circles doesn't melt the map.
// ─────────────────────────────────────────────────────────────────────────────

const LINES_SRC = 'cm-lines';
const FILLS_SRC = 'cm-fills';
const LAYER_FILL = 'cm-fill';
const LAYER_FILL_LINE = 'cm-fill-outline';
const LAYER_LINE = 'cm-line';
const LAYER_LINE_DASH = 'cm-line-dash';
/** Everything a TileLayer adds must sit BELOW this, so shapes always draw on top. */
const FIRST_OVERLAY_LAYER = LAYER_FILL;

class ShapeStore {
  private feats = new Map<string, GeoJSON.Feature>();
  private queued = false;
  constructor(private map: maplibregl.Map, private srcId: string) {}

  set(id: string, f: GeoJSON.Feature) { this.feats.set(id, f); this.flush(); }
  del(id: string) { if (this.feats.delete(id)) this.flush(); }

  private flush() {
    if (this.queued) return;
    this.queued = true;
    requestAnimationFrame(() => {
      this.queued = false;
      try {
        const src = this.map.getSource(this.srcId) as maplibregl.GeoJSONSource | undefined;
        if (!src) return;
        src.setData({ type: 'FeatureCollection', features: [...this.feats.values()] });
      } catch {
        /* map torn down between frames */
      }
    });
  }
}

type ClickHandler = (lngLat: maplibregl.LngLat) => void;

interface Ctx {
  map: maplibregl.Map;
  leaflet: LeafletMap;
  lines: ShapeStore;
  fills: ShapeStore;
  /** Shape id -> click handler. Populated by Polyline/Circle that have a Popup or eventHandlers. */
  clicks: Map<string, ClickHandler>;
  /** Street/Topo/Satellite/3D + day-night. Null until the manifest resolves. */
  basemap: BasemapController | null;
  manifest: LayersManifest | null;
}
const MapCtx = createContext<Ctx | null>(null);

/** The layer switcher's handle. Null until the manifest loads. */
export function useBasemap(): { ctl: BasemapController | null; manifest: LayersManifest | null } {
  const c = useContext(MapCtx);
  return { ctl: c?.basemap ?? null, manifest: c?.manifest ?? null };
}

export function useMap(): LeafletMap {
  const c = useContext(MapCtx);
  if (!c) throw new Error('useMap() must be used inside <MapContainer>');
  return c.leaflet;
}
function useCtx(): Ctx {
  const c = useContext(MapCtx);
  if (!c) throw new Error('map component must be used inside <MapContainer>');
  return c;
}

let uidCounter = 0;
function useUid(): string {
  const r = useRef<string>('');
  if (!r.current) { uidCounter += 1; r.current = 's' + uidCounter; }
  return r.current;
}

/** Leaflet dashArray is a CSS-ish string ("10 8", "8, 8"). We only need "is it dashed". */
function parseDash(dashArray?: string): boolean {
  if (!dashArray) return false;
  return dashArray.split(/[\s,]+/).map(Number).filter(x => !isNaN(x) && x > 0).length > 0;
}

/** Approximate a geodesic circle as a polygon so radius stays exact in METRES at any zoom. */
function circlePolygon(center: LatLng, radiusM: number, steps = 48): GeoJSON.Position[][] {
  const lat = center[0];
  const lng = center[1];
  const latR = radiusM / 111320;
  const lngR = radiusM / (111320 * Math.cos((lat * Math.PI) / 180) || 1);
  const ring: GeoJSON.Position[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = (i / steps) * 2 * Math.PI;
    ring.push([lng + lngR * Math.cos(t), lat + latR * Math.sin(t)]);
  }
  return [ring];
}

function installOverlayLayers(map: maplibregl.Map) {
  if (map.getSource(FILLS_SRC)) return;
  const empty: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

  map.addSource(FILLS_SRC, { type: 'geojson', data: empty });
  map.addSource(LINES_SRC, { type: 'geojson', data: empty });

  // Circles: fill + stroke.
  map.addLayer({
    id: LAYER_FILL, type: 'fill', source: FILLS_SRC,
    paint: {
      'fill-color': ['coalesce', ['get', 'fillColor'], '#3388ff'],
      'fill-opacity': ['coalesce', ['get', 'fillOpacity'], 0.2],
    },
  } as any);
  map.addLayer({
    id: LAYER_FILL_LINE, type: 'line', source: FILLS_SRC,
    paint: {
      'line-color': ['coalesce', ['get', 'color'], '#3388ff'],
      'line-width': ['coalesce', ['get', 'weight'], 1],
      'line-opacity': ['coalesce', ['get', 'opacity'], 1],
    },
  } as any);

  // Polylines: solid + dashed. line-dasharray can't be data-driven, hence two layers.
  const layout: any = { 'line-cap': 'round', 'line-join': 'round' };
  const paint: any = {
    'line-color': ['coalesce', ['get', 'color'], '#3388ff'],
    'line-width': ['coalesce', ['get', 'weight'], 3],
    'line-opacity': ['coalesce', ['get', 'opacity'], 1],
  };
  map.addLayer({
    id: LAYER_LINE, type: 'line', source: LINES_SRC,
    filter: ['!=', ['get', 'dashed'], true],
    layout, paint,
  } as any);
  map.addLayer({
    id: LAYER_LINE_DASH, type: 'line', source: LINES_SRC,
    filter: ['==', ['get', 'dashed'], true],
    layout,
    paint: { ...paint, 'line-dasharray': [2, 1.6] },
  } as any);
}

// ─────────────────────────────────────────────────────────────────────────────
// MapContainer
// ─────────────────────────────────────────────────────────────────────────────

export interface MapContainerProps {
  center: LatLng;
  zoom: number;
  maxZoom?: number;
  minZoom?: number;
  style?: CSSProperties;
  zoomControl?: boolean;
  children?: ReactNode;
  /** Override the shared style URL (tests / future themes). */
  styleUrl?: string;
}

export function MapContainer(props: MapContainerProps) {
  const { center, zoom, maxZoom = 22, minZoom = 2, style, zoomControl = true, children, styleUrl } = props;
  const holder = useRef<HTMLDivElement>(null);
  const [ctx, setCtx] = useState<Ctx | null>(null);

  useEffect(() => {
    if (!holder.current) return;
    let map: maplibregl.Map | null = null;
    let dead = false;

    loadSharedStyle(styleUrl || SHARED_STYLE_URL).then((style) => {
      if (dead || !holder.current) return;
      map = new maplibregl.Map({
        container: holder.current,
        style,
        center: [center[1], center[0]],
        zoom,
        maxZoom,
        minZoom,
        attributionControl: false,
      });
      wire(map);
    }).catch((e) => {
      // A blank map is worse than a loud one — if the style can't load, say so.
      // eslint-disable-next-line no-console
      console.error('[map] shared style failed to load:', e);
    });

    function wire(map: maplibregl.Map) {
    // Debug handle. Makes the live map inspectable from the console in prod —
    // which is exactly what you want the first time a dispatcher says "the map
    // looks wrong" and you have no idea which layer misbehaved.
    (window as any).__cmMap = map;

    map.addControl(new maplibregl.AttributionControl({ compact: true }), 'bottom-right');
    if (zoomControl !== false) {
      map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-left');
    }
    map.addControl(new maplibregl.ScaleControl({ unit: 'imperial' }), 'bottom-left');

    const clicks = new Map<string, ClickHandler>();

    const onLoad = () => {
      installOverlayLayers(map);

      const hit = (e: any) => {
        const f = e.features && e.features[0];
        const id = f && f.properties && (f.properties.__id as string);
        if (!id) return;
        const h = clicks.get(id);
        if (h) h(e.lngLat);
      };
      [LAYER_LINE, LAYER_LINE_DASH, LAYER_FILL].forEach((lid) => {
        map.on('click', lid, hit);
        map.on('mouseenter', lid, () => { map.getCanvas().style.cursor = 'pointer'; });
        map.on('mouseleave', lid, () => { map.getCanvas().style.cursor = ''; });
      });

      const base = {
        map,
        leaflet: new LeafletMap(map),
        lines: new ShapeStore(map, LINES_SRC),
        fills: new ShapeStore(map, FILLS_SRC),
        clicks,
      };
      setCtx({ ...base, basemap: null, manifest: null });
      // The layer manifest is a separate, non-blocking fetch: a map that draws is worth
      // more than a map that waits for its switcher. If it 404s the switcher just never
      // appears and the street map carries on.
      loadLayersManifest()
        .then((manifest) => setCtx({ ...base, manifest, basemap: new BasemapController(map, manifest) }))
        .catch((e) => console.warn('[map] layers manifest unavailable, switcher disabled:', e));
    };

    map.on('load', onLoad);
    }

    return () => {
      dead = true;
      setCtx(null);
      if (map) map.remove();
    };
    // Intentionally mount-once: react-leaflet's MapContainer ignores center/zoom
    // changes after mount too, and the pages rely on that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={holder} style={{ position: 'relative', ...style }}>
      {ctx ? <MapCtx.Provider value={ctx}>{children}</MapCtx.Provider> : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TileLayer — raster overlays (weather) and optional raster basemaps
// ─────────────────────────────────────────────────────────────────────────────

export interface TileLayerProps {
  url: string;
  opacity?: number;
  maxZoom?: number;
  attribution?: string;
  subdomains?: string[];
}

export function TileLayer(props: TileLayerProps) {
  const { url, opacity = 1, maxZoom = 19, attribution, subdomains } = props;
  const ctx = useCtx();
  const map = ctx.map;
  const uid = useUid();
  const srcId = 'tl-' + uid;
  const layerId = 'tl-' + uid + '-l';

  useEffect(() => {
    if (!url) return;
    const subs = subdomains || ['a', 'b', 'c'];
    const tiles = (url.indexOf('{s}') >= 0 ? subs.map(s => url.replace('{s}', s)) : [url])
      .map(t => (/^https?:\/\//i.test(t) ? t : new URL(t, window.location.origin).toString()));

    try {
      const src: any = { type: 'raster', tiles, tileSize: 256, maxzoom: Math.min(maxZoom, 22) };
      if (attribution) src.attribution = attribution;
      map.addSource(srcId, src);
      map.addLayer(
        { id: layerId, type: 'raster', source: srcId, paint: { 'raster-opacity': opacity } } as any,
        map.getLayer(FIRST_OVERLAY_LAYER) ? FIRST_OVERLAY_LAYER : undefined,
      );
    } catch {
      /* style torn down mid-flight */
    }

    return () => {
      try {
        if (map.getLayer(layerId)) map.removeLayer(layerId);
        if (map.getSource(srcId)) map.removeSource(srcId);
      } catch {
        /* map already gone */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, maxZoom, JSON.stringify(subdomains)]);

  useEffect(() => {
    try {
      if (map.getLayer(layerId)) map.setPaintProperty(layerId, 'raster-opacity', opacity);
    } catch {
      /* noop */
    }
  }, [opacity, map, layerId]);

  return null;
}

/**
 * The self-hosted basemap is now the VECTOR style itself, so selecting it in the
 * layer switcher means "add no raster layer at all". This component exists purely
 * so the LayersControl has something to mount for that option.
 */
export function VectorBaseLayer() { return null; }

// ─────────────────────────────────────────────────────────────────────────────
// Popup
// ─────────────────────────────────────────────────────────────────────────────

export function Popup(props: { maxWidth?: number; children?: ReactNode }) {
  const maxWidth = props.maxWidth || 300;
  return (
    <div className="cm-popup-body" style={{ maxWidth, font: '12px/1.4 system-ui, sans-serif' }}>
      {props.children}
    </div>
  );
}

/**
 * Renders `children` (a <Popup>) into a real maplibre Popup via a React portal, so
 * buttons inside popups (e.g. the "Navigate" button on the directions marker) keep
 * working — a renderToString approach would have silently killed them.
 */
function PortalPopup(props: {
  lngLat: [number, number];
  offset?: [number, number];
  children: ReactNode;
  onClose: () => void;
}) {
  const ctx = useCtx();
  const map = ctx.map;
  const [host] = useState(() => document.createElement('div'));
  const lng = props.lngLat[0];
  const lat = props.lngLat[1];

  useEffect(() => {
    const popup = new maplibregl.Popup({
      closeButton: true,
      closeOnClick: true,
      maxWidth: '340px',
      offset: props.offset || [0, -12],
    })
      .setLngLat([lng, lat])
      .setDOMContent(host)
      .addTo(map);
    popup.on('close', props.onClose);
    return () => { popup.remove(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lng, lat]);

  return createPortal(props.children, host);
}

// ─────────────────────────────────────────────────────────────────────────────
// Marker
// ─────────────────────────────────────────────────────────────────────────────

export interface MarkerProps {
  position: LatLng;
  icon?: IconDescriptor;
  children?: ReactNode;
  interactive?: boolean;
  eventHandlers?: { click?: () => void };
  zIndexOffset?: number;
}

export function Marker(props: MarkerProps) {
  const { position, icon, children, eventHandlers } = props;
  const interactive = props.interactive !== false;
  const ctx = useCtx();
  const map = ctx.map;
  const markerRef = useRef<maplibregl.Marker | null>(null);
  const elRef = useRef<HTMLDivElement | null>(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const ic = icon || DEFAULT_ICON;

  // Keep the latest click handler reachable from the DOM listener without
  // re-binding it on every render.
  const clickRef = useRef<(() => void) | undefined>(undefined);
  clickRef.current = eventHandlers && eventHandlers.click;

  // Create the marker element ONCE. Position and icon updates mutate it in place —
  // this is the hot path for live WS tracking (a tech marker re-renders on every
  // interpolation frame) and recreating the DOM node each time would visibly jank.
  useEffect(() => {
    const el = document.createElement('div');
    el.className = ic.className || 'cm';
    el.style.width = ic.iconSize[0] + 'px';
    el.style.height = ic.iconSize[1] + 'px';
    el.style.cursor = interactive ? 'pointer' : 'default';
    if (!interactive) el.style.pointerEvents = 'none';
    if (ic.html != null) {
      el.innerHTML = ic.html;
    } else if (ic.iconUrl) {
      const img = document.createElement('img');
      img.src = ic.iconUrl;
      img.width = ic.iconSize[0];
      img.height = ic.iconSize[1];
      el.appendChild(img);
    }
    elRef.current = el;

    // Leaflet iconAnchor = offset from the icon's TOP-LEFT to the anchor point.
    // MapLibre offset    = offset from the element's CENTRE to the anchor point.
    const offset: [number, number] = [
      ic.iconSize[0] / 2 - ic.iconAnchor[0],
      ic.iconSize[1] / 2 - ic.iconAnchor[1],
    ];

    const m = new maplibregl.Marker({ element: el, anchor: 'center', offset })
      .setLngLat([position[1], position[0]])
      .addTo(map);
    markerRef.current = m;

    if (interactive) {
      el.addEventListener('click', (ev) => {
        ev.stopPropagation();
        if (clickRef.current) clickRef.current();
        setPopupOpen(o => !o);
      });
    }
    return () => { m.remove(); markerRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Position updates — transform-only, no DOM churn.
  useEffect(() => {
    if (markerRef.current) markerRef.current.setLngLat([position[1], position[0]]);
  }, [position[0], position[1]]);

  // Icon html updates (speed badge, status dot, initials) — swap innerHTML in place.
  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    if (ic.html != null && el.innerHTML !== ic.html) {
      el.innerHTML = ic.html;
      el.style.width = ic.iconSize[0] + 'px';
      el.style.height = ic.iconSize[1] + 'px';
    }
  }, [ic.html, ic.iconSize[0], ic.iconSize[1]]);

  if (!children || !popupOpen) return null;
  const popOffset: [number, number] = ic.popupAnchor
    ? [ic.popupAnchor[0], ic.popupAnchor[1]]
    : [0, -Math.round(ic.iconAnchor[1] / 2)];
  return (
    <PortalPopup
      lngLat={[position[1], position[0]]}
      offset={popOffset}
      onClose={() => setPopupOpen(false)}
    >
      {children}
    </PortalPopup>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Polyline
// ─────────────────────────────────────────────────────────────────────────────

export interface PolylineProps {
  positions: LatLng[];
  pathOptions?: PathOptions;
  children?: ReactNode;
  eventHandlers?: { click?: () => void };
  interactive?: boolean;
}

export function Polyline(props: PolylineProps) {
  const { positions, children, eventHandlers } = props;
  const po = props.pathOptions || {};
  const ctx = useCtx();
  const uid = useUid();
  const [popupAt, setPopupAt] = useState<[number, number] | null>(null);

  useEffect(() => {
    if (!positions || positions.length < 2) { ctx.lines.del(uid); ctx.fills.del(uid + 'f'); return; }
    const coords = positions.map(p => [p[1], p[0]]);

    ctx.lines.set(uid, {
      type: 'Feature',
      properties: {
        __id: uid,
        color: po.color || '#3388ff',
        weight: po.weight == null ? 3 : po.weight,
        opacity: po.opacity == null ? 1 : po.opacity,
        dashed: parseDash(po.dashArray),
      },
      geometry: { type: 'LineString', coordinates: coords },
    });

    // Leaflet fills a closed polyline when fill:true. The NWS severe-weather alert
    // areas are drawn this way, so without this they'd be bare outlines with no
    // shading and the alertsOpacity slider would do nothing visible.
    if (po.fill) {
      const ring = coords.slice();
      const first = ring[0];
      const last = ring[ring.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) ring.push(first);
      ctx.fills.set(uid + 'f', {
        type: 'Feature',
        properties: {
          __id: '',                       // the LINE carries the click; don't double-fire
          color: po.color || '#3388ff',
          weight: 0,
          opacity: 0,
          fillColor: po.fillColor || po.color || '#3388ff',
          fillOpacity: po.fillOpacity == null ? 0.2 : po.fillOpacity,
        },
        geometry: { type: 'Polygon', coordinates: [ring] },
      });
    } else {
      ctx.fills.del(uid + 'f');
    }

    return () => { ctx.lines.del(uid); ctx.fills.del(uid + 'f'); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positions, po.color, po.weight, po.opacity, po.dashArray, po.fill, po.fillColor, po.fillOpacity]);

  useEffect(() => {
    ctx.clicks.set(uid, (lngLat) => {
      if (eventHandlers && eventHandlers.click) eventHandlers.click();
      if (children) setPopupAt([lngLat.lng, lngLat.lat]);
    });
    return () => { ctx.clicks.delete(uid); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children, eventHandlers]);

  if (!children || !popupAt) return null;
  return <PortalPopup lngLat={popupAt} onClose={() => setPopupAt(null)}>{children}</PortalPopup>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Circle  (GPS uncertainty radii, flight-trail altitude points)
// ─────────────────────────────────────────────────────────────────────────────

export interface CircleProps {
  center: LatLng;
  radius: number;                 // METRES, same as Leaflet
  pathOptions?: PathOptions;
  children?: ReactNode;
  interactive?: boolean;
}

export function Circle(props: CircleProps) {
  const { center, radius, children } = props;
  const po = props.pathOptions || {};
  const interactive = props.interactive !== false;
  const ctx = useCtx();
  const uid = useUid();
  const [popupAt, setPopupAt] = useState<[number, number] | null>(null);

  useEffect(() => {
    if (!center || center[0] == null || !radius) { ctx.fills.del(uid); return; }
    ctx.fills.set(uid, {
      type: 'Feature',
      properties: {
        // Non-interactive circles get an id that matches no handler, so a click
        // falls through to whatever is underneath — same as Leaflet's
        // interactive={false}.
        __id: interactive ? uid : '',
        color: po.color || '#3388ff',
        weight: po.weight == null ? 1 : po.weight,
        opacity: po.opacity == null ? 1 : po.opacity,
        fillColor: po.fillColor || po.color || '#3388ff',
        fillOpacity: po.fillOpacity == null ? 0.2 : po.fillOpacity,
      },
      geometry: { type: 'Polygon', coordinates: circlePolygon(center, radius) },
    });
    return () => ctx.fills.del(uid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center[0], center[1], radius, po.color, po.fillColor, po.fillOpacity, po.weight, po.opacity, interactive]);

  useEffect(() => {
    if (!interactive || !children) return;
    ctx.clicks.set(uid, (lngLat) => setPopupAt([lngLat.lng, lngLat.lat]));
    return () => { ctx.clicks.delete(uid); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children, interactive]);

  if (!children || !popupAt) return null;
  return <PortalPopup lngLat={popupAt} onClose={() => setPopupAt(null)}>{children}</PortalPopup>;
}

// ─────────────────────────────────────────────────────────────────────────────
// LayersControl — basemap switcher
// ─────────────────────────────────────────────────────────────────────────────

interface BaseLayerProps {
  name: string;
  checked?: boolean;
  children?: ReactNode;
}

/** Never rendered directly — LayersControl reads its props and mounts the winner. */
function BaseLayer(_props: BaseLayerProps): ReactElement | null {
  return null;
}

export interface LayersControlProps {
  position?: string;
  collapsed?: boolean;
  children?: ReactNode;
}

export function LayersControl(props: LayersControlProps) {
  const position = props.position || 'topright';
  const collapsed = props.collapsed !== false;

  const kids = Children.toArray(props.children).filter(isValidElement) as ReactElement<BaseLayerProps>[];
  const checkedKid = kids.filter(k => k.props.checked)[0];
  const initial = checkedKid ? checkedKid.props.name : (kids[0] ? kids[0].props.name : '');

  const [sel, setSel] = useState(initial);
  const [open, setOpen] = useState(!collapsed);

  const matched = kids.filter(k => k.props.name === sel)[0];
  const active = matched || kids[0];

  const pos: CSSProperties =
    position === 'topright' ? { top: 10, right: 10 } :
    position === 'topleft' ? { top: 10, left: 10 } :
    position === 'bottomright' ? { bottom: 34, right: 10 } :
    { bottom: 34, left: 10 };

  // Only one base to choose from (the normal case now that the vector style IS the
  // basemap): mount it, draw no chrome.
  if (kids.length <= 1) {
    return <Fragment>{active ? active.props.children : null}</Fragment>;
  }

  const boxStyle: CSSProperties = {
    position: 'absolute',
    zIndex: 500,
    background: '#fff',
    borderRadius: 4,
    boxShadow: '0 1px 5px rgba(0,0,0,.4)',
    padding: open ? '8px 10px' : '4px',
    font: '12px system-ui, sans-serif',
    cursor: 'pointer',
  };
  Object.assign(boxStyle, pos);

  return (
    <Fragment>
      {active ? <Fragment key={sel}>{active.props.children}</Fragment> : null}
      <div
        style={boxStyle}
        onMouseEnter={() => { if (collapsed) setOpen(true); }}
        onMouseLeave={() => { if (collapsed) setOpen(false); }}
      >
        {open ? (
          kids.map((k) => (
            <label
              key={k.props.name}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0', whiteSpace: 'nowrap' }}
            >
              <input
                type="radio"
                name={'cm-base-' + position}
                checked={sel === k.props.name}
                onChange={() => setSel(k.props.name)}
              />
              {k.props.name}
            </label>
          ))
        ) : (
          <div style={{ width: 26, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15 }}>
            &#9635;
          </div>
        )}
      </div>
    </Fragment>
  );
}

LayersControl.BaseLayer = BaseLayer;
